import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Dropdown, Field, Groupper, Header, Icon, Input, Message } from 'react-frontier';
import { SurveyFormQuestion, SurveyQuestionType } from '../SiteClasses';
import { surveyTypeHasValues } from '../SiteUtil';
import API from '../API';

import '../style/site.scss';

const YESNO_ITEMS = [
	{ text: 'Sí', value: '1' },
	{ text: 'No', value: '0' },
];

interface QuestionInputProps{
	question: SurveyFormQuestion,
	value: string,
	onChange: (question_id: number, value: string)=>void,
}

var QuestionInput = (props: QuestionInputProps)=>{
	var label = `${props.question.question}${props.question.required ? ' *' : ''}`;

	if(props.question.type_id===SurveyQuestionType.STATIC){
		return <Message type='info' header={props.question.question} style={{ marginBottom: 8 }}>
			{props.question.question_comment}
		</Message>
	}

	if(props.question.type_id===SurveyQuestionType.YESNO){
		return <Dropdown
			label={label}
			comment={props.question.question_comment}
			items={YESNO_ITEMS}
			value={props.value || null}
			onValueChange={v=>props.onChange(props.question.question_id, v || '')}
		/>
	}

	if(props.question.type_id===SurveyQuestionType.MULTIPLE){
		return <Field label={label} comment={props.question.question_comment}>
			{(props.question.values || []).map(a=>(
				<label key={`PSVOPT-${a.value_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
					<input
						type='radio'
						name={`PSVQ-${props.question.question_id}`}
						checked={props.value===a.value_id.toString()}
						onChange={()=>props.onChange(props.question.question_id, a.value_id.toString())}
					/>
					{a.value}
				</label>
			))}
		</Field>
	}

	if(surveyTypeHasValues(props.question.type_id)){
		return <Dropdown
			label={label}
			comment={props.question.question_comment}
			items={(props.question.values || []).map(a=>({ text: a.value, value: a.value_id.toString() }))}
			value={props.value || null}
			onValueChange={v=>props.onChange(props.question.question_id, v || '')}
		/>
	}

	return <Input
		fluid
		label={label}
		comment={props.question.question_comment}
		textarea={props.question.type_id===SurveyQuestionType.TEXTAREA}
		textareaRows={3}
		inputType={props.question.type_id===SurveyQuestionType.DATE ? 'date' : undefined}
		value={props.value ?? ''}
		onChange={v=>props.onChange(props.question.question_id, v)}
		style={{ marginBottom: 8 }}
	/>
}

var PublicSurveyPage = ()=>{
	var { token } = useParams<{ token: string }>();
	var [questions, setQuestions] = useState<SurveyFormQuestion[]>(null);
	var [answers, setAnswers] = useState<{ [question_id: number]: string }>({});
	var [loadError, setLoadError] = useState<string>(null);
	var [submitError, setSubmitError] = useState<string>(null);
	var [submitting, setSubmitting] = useState<boolean>(false);
	var [sent, setSent] = useState<boolean>(false);

	useEffect(()=>{
		if(!token) return;
		setLoadError(null);
		API.getPublicSurvey(token).then(res=>{
			if(res.error) return setLoadError(res.message);
			var data: SurveyFormQuestion[] = res.data || [];
			setQuestions(data);
			var init: { [k: number]: string } = {};
			for(var q of data){
				init[q.question_id] = surveyTypeHasValues(q.type_id) ? '' : (
					q.type_id===SurveyQuestionType.YESNO ? (['0', '1'].indexOf(q.default_value)!==-1 ? q.default_value : '') : (q.default_value || '')
				);
			}
			setAnswers(init);
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando la encuesta. (PSVP-1)');
		});
	}, [token]);

	var onAnswerChange = (question_id: number, value: string)=>{
		setAnswers(prev=>({ ...prev, [question_id]: value }));
	}

	var submit = ()=>{
		if(submitting || !questions) return;
		setSubmitError(null);

		var missing = questions.filter(q=>q.required && q.type_id!==SurveyQuestionType.STATIC && !(answers[q.question_id] || '').trim());
		if(missing.length>0){
			return setSubmitError(`Falta contestar: ${missing.map(a=>a.question).join(', ')}`);
		}

		var payload = questions.filter(q=>q.type_id!==SurveyQuestionType.STATIC).map(q=>{
			var val = (answers[q.question_id] || '').trim();
			if(!val) return null;
			if(surveyTypeHasValues(q.type_id)) return { question_id: q.question_id, value_id: parseInt(val) };
			return { question_id: q.question_id, answer: val };
		}).filter(a=>!!a);

		if(payload.length===0){
			return setSubmitError('No has contestado ninguna pregunta.');
		}

		setSubmitting(true);
		API.answerPublicSurvey(token, payload).then(res=>{
			if(res.error) return setSubmitError(res.message);
			setSent(true);
		}).catch(()=>{
			setSubmitError('Hubo un error inesperado enviando la encuesta. (PSVP-2)');
		}).finally(()=>{
			setSubmitting(false);
		});
	}

	if(!token){
		return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
			<Message type='error' header='Enlace inválido' style={{ maxWidth: 500 }}>
				El enlace de la encuesta no es válido. Por favor solicita uno nuevo.
			</Message>
		</div>
	}

	return <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px 16px' }}>
		<Groupper 
			title='Encuesta AREMA' 
			style={{ maxWidth: 680, margin: 'auto', marginBottom: 40 }}
			actions={!sent ?
				<>
					<Button text='Enviar' color='black' loading={submitting} onClick={submit} />
				</> : null
			}
		>
			{sent ? (
				<div style={{ textAlign: 'center', padding: 20 }}>
					<Icon name='check-circle' size={80} style={{ marginBottom: 100}} color='green'/>
					<Header text='¡Encuesta enviada!' subtext='Gracias por tu colaboración.' style={{ marginTop: 20}}/>
				</div>
			) : (<>
				{!!loadError && <Message type='error' header='Error' style={{ margin: 16 }}>{loadError}</Message>}
				{!!submitError && <Message type='error' header='Error al enviar' style={{ margin: 16 }}>{submitError}</Message>}
				{!questions && !loadError && (
					<div style={{ padding: 40 }}>
						<Header loading text='Cargando encuesta' />
					</div>
				)}
				{questions && questions.length===0 && (
					<div style={{ padding: 40 }}>
						<Header text='Sin preguntas' subtext='La encuesta no tiene preguntas configuradas.' />
					</div>
				)}
				{questions && questions.length>0 && (<>
					<div style={{ padding: '0 16px' }}>
						{questions.map(q=>(
							<div key={`PUBSVQ-${q.question_id}`} style={{ marginBottom: 16 }}>
								<QuestionInput
									question={q}
									value={answers[q.question_id] ?? ''}
									onChange={onAnswerChange}
								/>
							</div>
						))}
					</div>
				</>)}
			</>)}
		</Groupper>
	</div>
}

export default PublicSurveyPage;
